import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  DarkTheme,
  DefaultTheme,
  ThemeProvider as NavigationThemeProvider,
} from 'expo-router/react-navigation';
import { GluestackUIProvider } from '../gluestack/gluestack-ui-provider';
import { Platform, useColorScheme } from 'react-native';
import { SystemBars } from 'react-native-edge-to-edge';
import { vars } from 'nativewind';
import { nativewindThemes } from '@/constants/nativewindThemes';
import { getCommunityFromDomain, communities } from '@/constants/communities';
import { patterns, PatternName, Pattern } from '@/constants/patterns';
import { ToastHost } from '@/components/gluestack/toast';
import { useAuth } from './AuthProvider';

export type ThemeMode = 'auto' | 'light' | 'dark';
type EffectiveColorScheme = 'light' | 'dark';
export type ThemeName = keyof typeof nativewindThemes;

// currentTheme is deliberately read-only for consumers: the global theme has a
// single owner — the derivation effect below (currentCommunity → lastUsed →
// domain → default). Screens that need another community's branding scope it
// locally with CommunityThemeScope instead of flipping the global theme.
type ThemeContextType = {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  currentTheme: ThemeName;
  effectiveColorScheme: 'light' | 'dark';
  getThemeColor: (
    colorName: string,
    shade?: string,
    overrideColorScheme?: 'light' | 'dark'
  ) => string;
  currentPattern: Pattern;
  setPatternName: (pattern: PatternName) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  themeMode: 'auto',
  setThemeMode: () => {},
  currentTheme: 'default', // Start with default, effects will set the correct one
  effectiveColorScheme: 'light',
  getThemeColor: () => '',
  currentPattern: patterns.polygonal,
  setPatternName: () => {},
});

const normalizeColorScheme = (
  colorScheme: ReturnType<typeof useColorScheme>
): EffectiveColorScheme => (colorScheme === 'dark' ? 'dark' : 'light');

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);
  const [themeMode, setThemeModeState] = useState<ThemeMode>('auto');
  // Initialize with default, let effects determine the actual theme
  const [currentTheme, setCurrentTheme] = useState<ThemeName>('default');
  const [communityPatterns, setCommunityPatterns] = useState<Record<string, PatternName>>({});
  const systemColorScheme = normalizeColorScheme(useColorScheme());
  // Use currentCommunity, lastUsedCommunity and isLoading from useAuth
  const { currentCommunity, lastUsedCommunity, isLoading } = useAuth();

  const effectiveColorScheme = themeMode === 'auto' ? systemColorScheme : themeMode;

  // Effect to load user preferences (mode, pattern) from storage
  useEffect(() => {
    (async () => {
      const [storedMode, storedPatternsJson] = await Promise.all([
        AsyncStorage.getItem('themeMode'),
        AsyncStorage.getItem('communityPatterns'),
      ]);

      if (storedMode === 'light' || storedMode === 'dark' || storedMode === 'auto') {
        setThemeModeState(storedMode);
      }

      if (storedPatternsJson) {
        try {
          const parsedPatterns = JSON.parse(storedPatternsJson);
          setCommunityPatterns(parsedPatterns);
        } catch (e) {
          console.error('Failed to parse community patterns', e);
        }
      } else {
        // Fallback: try to read legacy patternName and migrate it if necessary?
        // For now, we prioritize fresh start with defaults per requirement.
      }

      // Mark theme preferences as loaded, but wait for auth state too
      // setIsThemeLoaded(true); // Defer this until auth state is also considered
    })();
  }, []);

  // Effect to set the theme based on auth state or domain
  useEffect(() => {
    // Only run when auth state is determined (isLoading is false)
    if (!isLoading) {
      let determinedTheme: ThemeName = 'default';

      // 1. Prioritize current authenticated community
      if (currentCommunity && currentCommunity in nativewindThemes) {
        determinedTheme = currentCommunity as ThemeName;
      }
      // 2. Fallback to last used community (useful for sign-in screen after sign-out)
      else if (lastUsedCommunity && lastUsedCommunity in nativewindThemes) {
        determinedTheme = lastUsedCommunity as ThemeName;
      }
      // 3. Fallback to domain-specific theme
      else {
        const domainTheme = getCommunityFromDomain();
        if (domainTheme && domainTheme in nativewindThemes) {
          determinedTheme = domainTheme;
        }
        // 4. Final fallback to 'default' is implicitly handled by the initial state
      }

      setCurrentTheme(determinedTheme);
      setIsThemeLoaded(true); // Mark theme as fully loaded now
    }
    // Rerun when currentCommunity, lastUsedCommunity, or isLoading changes
  }, [currentCommunity, lastUsedCommunity, isLoading]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    AsyncStorage.setItem('themeMode', mode);
  }, []);

  const setPatternName = useCallback(
    (pattern: PatternName) => {
      setCommunityPatterns((prev) => {
        const next = { ...prev, [currentTheme]: pattern };
        AsyncStorage.setItem('communityPatterns', JSON.stringify(next));
        return next;
      });
    },
    [currentTheme]
  );

  // Determine current pattern based on theme/community
  const currentPatternName: PatternName = useMemo(() => {
    // 1. Check if user has set a preference for this community/theme
    if (communityPatterns[currentTheme]) {
      return communityPatterns[currentTheme];
    }

    // 2. Check for community default
    // currentTheme matches community codes (e.g., 'grommr', 'feabie')
    if (currentTheme in communities) {
      const communityConfig = communities[currentTheme as keyof typeof communities];
      if (communityConfig.defaultPattern) {
        return communityConfig.defaultPattern;
      }
    }

    // 3. Fallback to default
    return 'polygonal';
  }, [currentTheme, communityPatterns]);

  const themeVars =
    nativewindThemes[currentTheme]?.[effectiveColorScheme] ??
    nativewindThemes['default'][effectiveColorScheme];

  const getThemeColor = useCallback(
    (colorName: string, shade: string = '500', overrideColorScheme?: 'light' | 'dark') => {
      const colorScheme = overrideColorScheme || effectiveColorScheme;
      const theme =
        nativewindThemes[currentTheme]?.[colorScheme] ?? nativewindThemes['default'][colorScheme];
      const colorKey = `--color-${colorName}-${shade}`;
      const colorValue = theme[colorKey as keyof typeof theme] ?? '0 0 0';
      const [r, g, b] = colorValue.split(' ').map(Number);
      return `rgb(${r}, ${g}, ${b})`;
    },
    [currentTheme, effectiveColorScheme]
  );

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const theme = nativewindThemes[currentTheme] ?? nativewindThemes['default'];

    // COLORS CONFIG ON WEB: Apply theme color variables to :root
    Object.entries(theme[effectiveColorScheme]).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    // Safari 15–18: theme-color meta tag controls the browser chrome color
    // Safari 26+: ignores theme-color; instead samples fixed/sticky elements near viewport edges
    const themeColor = getThemeColor('secondary', '500');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', themeColor);
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'theme-color';
      newMeta.content = themeColor;
      document.head.appendChild(newMeta);
    }

    // Per-community branding (favicon, apple-touch-icon, meta tags).
    // Reinforces the inline <head> script and handles dynamic community switches.
    const communityConfig =
      currentTheme in communities
        ? communities[currentTheme as keyof typeof communities]
        : undefined;
    if (communityConfig) {
      const code = communityConfig.code;
      const setAttr = (sel: string, attr: string, val: string) => {
        const el = document.querySelector(sel);
        if (el) el.setAttribute(attr, val);
      };
      setAttr('link[rel="manifest"]', 'href', `/brand/${code}/manifest.json`);
      setAttr('link[rel="icon"]', 'href', `/brand/${code}/favicon.png`);
      setAttr('link[rel="apple-touch-icon"]', 'href', `/brand/${code}/apple-touch-icon.png`);
      setAttr('meta[name="apple-mobile-web-app-title"]', 'content', communityConfig.name);
      setAttr('meta[name="description"]', 'content', communityConfig.webDescription);
    }

    // Body background for overscroll areas on mobile Safari
    const navTheme = effectiveColorScheme === 'dark' ? DarkTheme : DefaultTheme;
    document.body.style.backgroundColor = navTheme.colors.card as string;
  }, [currentTheme, effectiveColorScheme, getThemeColor]);

  const providerValue = useMemo<ThemeContextType>(
    () => ({
      themeMode,
      setThemeMode,
      currentTheme,
      effectiveColorScheme,
      getThemeColor,
      currentPattern: patterns[currentPatternName],
      setPatternName,
    }),
    [themeMode, setThemeMode, currentTheme, effectiveColorScheme, getThemeColor, currentPatternName]
  );

  if (!isThemeLoaded) {
    // Waiting for isThemeLoaded (which now depends on auth state)
    return null;
  }

  return (
    <ThemeContext.Provider value={providerValue}>
      <NavigationThemeProvider value={effectiveColorScheme === 'dark' ? DarkTheme : DefaultTheme}>
        <GluestackUIProvider mode={effectiveColorScheme} style={vars(themeVars)}>
          {/* See GluestackUI color definitions in: @/components/gluestack/gluestack-ui-provider/config.ts */}
          {/* See color definitions in: @/constants/nativewindThemes.ts */}
          <SystemBars style={'light'} />
          {children}
          {/*
            ToastHost must stay inside GluestackUIProvider's OverlayProvider: it registers
            an overlay at show time, which is what paints toasts above already-open
            overlay elements (Drawer, Modal, ActionSheet, image viewer).
          */}
          <ToastHost />
        </GluestackUIProvider>
      </NavigationThemeProvider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
